import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAnalyticsUtilities, useTrackViewedBoard } from "@air/analytics";
import { ActionBarProvider } from "@air/provider-action-bar";
import { ModalProvider, useAirModal } from "@air/provider-modal";
import { ShortIdProvider, useShortIdContext } from "@air/provider-short-id";
import { useToasts } from "@air/provider-toast";
import { ErrorBoundary } from "@air/utils-error";
import { ConnectedRouter } from "connected-next-router";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { usePrevious, useUnmount } from "__barrel_optimize__?names=usePrevious,useUnmount!=!react-use";
import { AccountProvisionerProvider } from "~/components/AccountProvisioner/providers/AccountProvisionerProvider";
import { usePublicPageViewNotification } from "~/components/AirNotifications/hooks/usePublicPageViewNotification";
import { PublicFiltersReduxObserver } from "~/components/Filters/PublicFiltersReduxObserver";
import { usePublicBoardPage } from "~/components/Layouts/PublicBoardLayout/hooks/usePublicBoardPage";
import { MaintenanceMode } from "~/components/MaintenanceMode/MaintenanceMode";
import { PublicBoardReduxProvider } from "~/components/PublicBoardReduxProvider";
import { PublicBoardSocketContextProvider } from "~/components/PublicBoardSocketContextProvider";
import { PublicBoardAnonymousSignUpModal } from "~/components/PublicLinkAnonymousSignUpModal/PublicBoardAnonymousSignUpModal";
import { PublicPermissionsProvider } from "~/components/PublicPermissionsProvider";
import { usePublicBoardSSOCallback } from "~/components/PublicSSOCallback/usePublicBoardSSOCallback";
import { PublicWorkspaceProvider } from "~/components/PublicWorkspaceProvider";
import { useOnLocationChange } from "~/hooks/router/useOnLocationChange";
import { useOnMissingUserSession } from "~/hooks/useOnMissingUserSession";
import { ThumbnailPreferenceContextProvider } from "~/hooks/useThumbnailPreference";
import { useURLBoardIdSelector } from "~/hooks/useURLBoardIdSelector";
import { useURLClipIdSelector } from "~/hooks/useURLClipIdSelector";
import { PublicAccountProvider, useAccountContext } from "~/providers/AccountProvider";
import { FiltersProvider } from "~/providers/FiltersProvider";
import { ModifiersKeysPressedProvider } from "~/providers/ModifiersKeysPressedProvider";
import { PublicBoardCustomFieldsProvider } from "~/providers/PublicBoardCustomFieldsProvider";
import { SearchHeaderHeightProvider } from "~/providers/SearchHeaderHeightProvider";
import { resetCentralizedBoardAction } from "~/store/centralizedBoard/actions";
import { resetConfigViewsAction, setCurrentViewTypeAction, setSavedConfigurableViewsAction } from "~/store/configViews/actions";
import { currentViewTypeNameSelector, savedConfigurableViewsSelector } from "~/store/configViews/selectors";
import { getBoardIdFromPath } from "~/utils/PathUtils";
import { useAirStore } from "~/utils/ReduxUtils";
import { PublicBoardSEO } from "./PublicBoardSEO";
import { ViewableBoard } from "./ViewableBoard";
const PublicBoardInner = (param)=>{
    let { initialData, boardId: boardIdFromProps } = param;
    const dispatch = useDispatch();
    const store = useAirStore();
    const urlBoardId = useURLBoardIdSelector();
    const boardId = urlBoardId || boardIdFromProps;
    const previousBoardId = usePrevious(boardId);
    const clipId = useURLClipIdSelector();
    const { shortId } = useShortIdContext();
    const { trackViewedBoard } = useTrackViewedBoard();
    const { setGlobalEventProps } = useAnalyticsUtilities();
    const { dismissAllToasts } = useToasts();
    const { data: account } = useAccountContext();
    usePublicBoardSSOCallback();
    const [showAnonymousSignupModal] = useAirModal(PublicBoardAnonymousSignUpModal);
    const { sendPublicPageViewEvent } = usePublicPageViewNotification();
    const requireAccount = initialData.requireAccount;
    useOnMissingUserSession({
        onMissingSession: ()=>showAnonymousSignupModal({}),
        enabled: requireAccount
    });
    useEffect(()=>{
        if (!requireAccount || requireAccount && !!account) {
            sendPublicPageViewEvent(shortId);
        }
    }, [
        account,
        requireAccount,
        sendPublicPageViewEvent,
        shortId
    ]);
    const { data: board } = usePublicBoardPage({
        boardId,
        initialData: boardId === initialData.board.id ? {
            ...initialData.board,
            ancestors: []
        } : undefined
    });
    useEffect(()=>{
        setGlobalEventProps({
            // workspace information whether the user is logged in or not
            workspaceId: initialData.workspace.id,
            workspaceName: initialData.workspace.name
        });
    }, [
        setGlobalEventProps,
        initialData.workspace.id,
        initialData.workspace.name
    ]);
    useEffect(()=>{
        if ((board === null || board === void 0 ? void 0 : board.id) && !clipId) {
            var _board_views_find, _board_views;
            trackViewedBoard({
                board_id: board.id,
                board_title: board.title,
                isDemo: board.isDemo,
                board_view: (_board_views = board.views) === null || _board_views === void 0 ? void 0 : (_board_views_find = _board_views.find((param)=>{
                    let { isDefault } = param;
                    return !!isDefault;
                })) === null || _board_views_find === void 0 ? void 0 : _board_views_find.viewType.name
            });
        }
    // We only want to track when a user navigates to a new board (or on load)
    // so we want the id instead of the entire board object cause a board could
    // get modified in local cache (unlikely in a public board but better to be safe)
    // we watch clipId because we don't want to consider it a board view if they're
    // just looking at the asset modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        board === null || board === void 0 ? void 0 : board.id,
        clipId
    ]);
    useEffect(()=>{
        if (previousBoardId && previousBoardId !== boardId) {
            dispatch(resetConfigViewsAction());
        }
    }, [
        boardId,
        dispatch,
        previousBoardId
    ]);
    useEffect(()=>{
        if (!!(board === null || board === void 0 ? void 0 : board.views)) {
            const currentViews = savedConfigurableViewsSelector(store.getState());
            const currentViewTypeName = currentViewTypeNameSelector(store.getState());
            const boardViews = board.views;
            dispatch(setSavedConfigurableViewsAction({
                savedConfigurableViews: boardViews
            }));
            if (!!currentViews && !!currentViewTypeName) {
                dispatch(setCurrentViewTypeAction({
                    viewTypeName: currentViewTypeName
                }));
            }
        }
    }, [
        dispatch,
        board === null || board === void 0 ? void 0 : board.views,
        store
    ]);
    useOnLocationChange(dismissAllToasts);
    // this can't really happen on a public board URL cause where can the user go?
    // but just for safe measure and future-proofing
    useUnmount(()=>{
        dispatch(resetCentralizedBoardAction());
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            board && /*#__PURE__*/ _jsx(PublicBoardSEO, {
                board: board
            }),
            /*#__PURE__*/ _jsx(ViewableBoard, {})
        ]
    });
};
export const PublicBoard = (param)=>{
    let { initialData, shortId, boardId } = param;
    const { asPath } = useRouter();
    const { name: workspaceName, image: workspaceImage, id: workspaceId } = initialData.workspace;
    const getImmutableFilters = useCallback(()=>({
            boardId: getBoardIdFromPath(asPath)
        }), [
        asPath
    ]);
    return /*#__PURE__*/ _jsx(AccountProvisionerProvider, {
        children: /*#__PURE__*/ _jsx(PublicAccountProvider, {
            children: /*#__PURE__*/ _jsx(PublicPermissionsProvider, {
                permissions: initialData.permissions,
                children: /*#__PURE__*/ _jsx(PublicBoardReduxProvider, {
                    initialBoard: initialData.board,
                    children: /*#__PURE__*/ _jsx(ShortIdProvider, {
                        shortId: shortId,
                        objectId: initialData.board.id,
                        objectType: "board",
                        children: /*#__PURE__*/ _jsx(PublicWorkspaceProvider, {
                            workspaceId: workspaceId,
                            workspaceImage: workspaceImage,
                            workspaceName: workspaceName,
                            children: /*#__PURE__*/ _jsx(ErrorBoundary, {
                                children: /*#__PURE__*/ _jsx(ConnectedRouter, {
                                    children: /*#__PURE__*/ _jsx(SearchHeaderHeightProvider, {
                                        children: /*#__PURE__*/ _jsx(FiltersProvider, {
                                            getImmutableFilters: getImmutableFilters,
                                            children: /*#__PURE__*/ _jsx(ModifiersKeysPressedProvider, {
                                                children: /*#__PURE__*/ _jsx(ModalProvider, {
                                                    children: /*#__PURE__*/ _jsx(ThumbnailPreferenceContextProvider, {
                                                        children: /*#__PURE__*/ _jsx(MaintenanceMode, {
                                                            children: /*#__PURE__*/ _jsx(PublicBoardCustomFieldsProvider, {
                                                                children: /*#__PURE__*/ _jsx(PublicBoardSocketContextProvider, {
                                                                    children: /*#__PURE__*/ _jsxs(ActionBarProvider, {
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx(PublicBoardSEO, {
                                                                                board: initialData.board
                                                                            }),
                                                                            /*#__PURE__*/ _jsx(PublicFiltersReduxObserver, {}),
                                                                            /*#__PURE__*/ _jsx(PublicBoardInner, {
                                                                                initialData: initialData,
                                                                                boardId: boardId
                                                                            })
                                                                        ]
                                                                    })
                                                                })
                                                            })
                                                        })
                                                    })
                                                })
                                            })
                                        })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        })
    });
};
